import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatOptgroup, MatOption, MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import {
  catchError,
  filter,
  finalize,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { DialogService } from '../../components/dialog/dialog.service';
import { HasPermissionDirective, PermissionService } from '@ipg/ngx-frontend-toolset';
import { Client, ClientConfig, InteractionPriority } from '../../models/clients.model';
import { ClientVarResponse, VarService } from '../../services/var.service';
import { ClientStateService } from '../clients/services/client-state.service';
import { ClientsService } from '../clients/services/clients.service';
import {
  AnnouncementSound,
  AnnouncementSoundsService,
} from './services/announcement-sounds.service';
import { FACE_COLORS, FACE_VARIANTS } from './face-variant.config';
import { FaceColor, FaceVariant } from '../../models/clients.model';
import { DEFAULT_LANG, DEFAULT_VOICE, VOICE_CATALOG, VoiceMeta } from './voice.config';
import { TtsPlayerService } from '../../services/tts-player.service';

interface ClientSettingsModel {
  name: string;
  nickname: string;
  config: ClientConfig;
}

@Component({
  selector: 'app-client-settings',
  imports: [
    MatButton,
    MatError,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatOptgroup,
    MatOption,
    MatRadioButton,
    MatRadioGroup,
    MatSelect,
    MatSlideToggle,
    MatSliderModule,
    MatSuffix,
    ReactiveFormsModule,
    HasPermissionDirective,
  ],
  templateUrl: './client-settings.component.html',
  styleUrl: './client-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientSettingsComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _route = inject(ActivatedRoute);
  private readonly _clients = inject(ClientsService);
  private readonly _clientStateService = inject(ClientStateService);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _announcementSounds = inject(AnnouncementSoundsService);
  private readonly _varService = inject(VarService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _ttsPlayer = inject(TtsPlayerService);
  protected readonly isVoicePreviewing = this._ttsPlayer.isPlaying;

  private readonly _audioPlayer =
    viewChild<ElementRef<HTMLAudioElement>>('audioPlayer');

  protected readonly sounds = signal<AnnouncementSound[]>([]);
  protected readonly playingSoundId = signal<string | null>(null);

  /** Face variant and color options for the picker UI */
  protected readonly faceVariants = FACE_VARIANTS;
  protected readonly faceColors = FACE_COLORS;

  /** Voice options — reactive based on selected language */
  protected readonly voiceCatalog = VOICE_CATALOG;
  protected readonly maleVoices = signal<VoiceMeta[]>([]);
  protected readonly femaleVoices = signal<VoiceMeta[]>([]);
  protected readonly voicePreviewText = signal('');

  /** All base vars merged with their client overrides */
  protected readonly clientVars = signal<ClientVarResponse[]>([]);

  /**
   * The variables section is an enhancement on a page the client workspace's
   * own `client:read` already entitles the user to -- this route carries no
   * guard of its own -- but `/clients/{id}/vars` behind it needs `var:read`.
   * Without that permission the section stays empty (the template hides it)
   * and the call is never made, rather than answering 403 and putting up a
   * missing-permission notice for a section the role was never meant to show.
   */
  protected readonly canReadVars = computed(() =>
    this._permissionService.hasPermission('var:read')
  );

  /**
   * Overrides are saved along with the client, so editing them takes
   * `client:update` as well as `var:update`. The save replaces the client's
   * whole override set, which is why it also needs the `var:read` that loaded
   * it: sent without it, the set went back empty and every override was
   * deleted. Without all three the inputs are read-only and nothing is sent.
   */
  protected readonly canEditVars = computed(
    () =>
      this.canReadVars() &&
      this._permissionService.hasPermission('var:update') &&
      this._permissionService.hasPermission('client:update')
  );

  /**
   * What each edited override field holds, by baseVarId, as typed. Kept apart
   * from the fields' `[value]`, which shows the saved state only: fed back
   * into it, an edit passing through the base value (typing "Hallo daar" over
   * base "Hallo") rewrote the field to empty mid-typing.
   */
  private readonly _varEdits = signal<Map<string, string>>(new Map());

  /** True when an edit changes what is saved */
  protected readonly hasVarChanges = computed(() =>
    this.clientVars().some(
      (v) => this._overrideAfterEdits(v) !== this._savedOverride(v)
    )
  );

  private readonly _clientId = this._route.parent?.snapshot.paramMap.get('clientId');

  private readonly _client$ = of(this._clientId).pipe(
    filter((clientId) => clientId != null),
    switchMap((clientId) => this._clients.getById(clientId)),
    map((result) => this.mapFromResponse(result))
  );

  protected readonly form = this._fb.group({
    name: this._fb.control('', [Validators.required, Validators.maxLength(100)]),
    nickname: this._fb.control('', [Validators.required, Validators.maxLength(100)]),
    config: this._fb.group({
      waitForAnswer: this._fb.control(2, [
        Validators.min(2),
        Validators.max(5),
        Validators.pattern(/^\d+$/),
        Validators.required,
      ]),
      notify: this._fb.control(false),
      notifySoundId: this._fb.control<string | null>(null),
      volume: this._fb.control(50, [Validators.min(0), Validators.max(100)]),
      faceVariant: this._fb.control<FaceVariant>('face_1', [Validators.required]),
      faceColor: this._fb.control<FaceColor>('blue', [Validators.required]),
      allowVolumeControl: this._fb.control(false),
      announceBatteryLevel: this._fb.control(false),
      ttsVoice: this._fb.control(DEFAULT_VOICE),
      ttsLang: this._fb.control(DEFAULT_LANG),
      interactionPriority: this._fb.control<InteractionPriority>('scm', [Validators.required]),
    }),
  });

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  get controls() {
    return this.form.controls;
  }

  get configControls() {
    return this.controls.config.controls;
  }

  constructor() {
    this._updateVoicesForLang(DEFAULT_LANG);

    this.configControls.ttsLang.valueChanges.subscribe((lang) => {
      this._updateVoicesForLang(lang);

      const langEntry = VOICE_CATALOG.find((l) => l.lang === lang);
      const currentVoice = this.configControls.ttsVoice.value;
      const isVoiceValid = langEntry?.voices.some((v) => v.value === currentVoice);

      // Only auto-select a default voice if the current voice is invalid for the new language
      if (!isVoiceValid) {
        const firstVoice =
          langEntry?.voices.find((v) => v.gender === 'female') ??
          langEntry?.voices[0];
        if (firstVoice) {
          this.configControls.ttsVoice.setValue(firstVoice.value);
        }
      }
    });

    this._client$.subscribe({
      next: (value) => {
        if (value.config?.ttsLang) {
          this._updateVoicesForLang(value.config.ttsLang);
        }
        this.form.patchValue(value);
      },
    });

    this.configControls.notify.valueChanges.subscribe((notify) => {
      const soundControl = this.configControls.notifySoundId;
      if (notify) {
        soundControl.setValidators(Validators.required);
        // Auto-select the default sound when toggling on if nothing is selected yet
        if (!soundControl.value) {
          const defaultSound = this.sounds().find((s) => s.isDefault);
          if (defaultSound) {
            soundControl.setValue(defaultSound._id);
          }
        }
      } else {
        soundControl.clearValidators();
        soundControl.setValue(null);
      }
      soundControl.updateValueAndValidity();
    });

    this._announcementSounds
      .getAll({ params: { limit: 100, sortBy: 'label', sortOrder: 'asc' } })
      .subscribe({
        next: (response) => {
          this.sounds.set(response.items);

          setTimeout(() => {
            const currentSoundId = this.configControls.notifySoundId.value;
            if (currentSoundId) {
              this.configControls.notifySoundId.setValue(currentSoundId);
            } else if (this.configControls.notify.value) {
              // Auto-select the default sound when notify is enabled
              // but no specific sound has been selected
              const defaultSound = response.items.find((s) => s.isDefault);
              if (defaultSound) {
                this.configControls.notifySoundId.setValue(defaultSound._id);
              }
            }
          });
        },
      });

    if (!this._permissionService.hasPermission('client:update')) {
      this.form.disable();
    }

    // Load client vars, once `var:read` is known to be held. Waiting on the
    // signal rather than reading it here is deliberate: permissions arrive
    // with the user fetch, which `authGuard` does not await, so a plain read
    // at construction time is routinely false for a user who does have it.
    if (this._clientId) {
      toObservable(this.canReadVars)
        .pipe(
          filter(Boolean),
          take(1),
          switchMap(() => this._varService.getClientVars(this._clientId!)),
          takeUntilDestroyed()
        )
        .subscribe((vars) => this.clientVars.set(vars));
    }
  }

  playSound(sound: AnnouncementSound): void {
    const player = this._audioPlayer()?.nativeElement;
    if (!player) return;

    if (this.playingSoundId() === sound._id) {
      player.pause();
      player.currentTime = 0;
      this.playingSoundId.set(null);
      return;
    }

    player.src = this._announcementSounds.getFileUrl(sound.fileId);
    player.play();
    this.playingSoundId.set(sound._id);

    player.onended = () => this.playingSoundId.set(null);
  }

  previewVoice(): void {
    const voice = this.configControls.ttsVoice.value;
    const lang = this.configControls.ttsLang.value;
    this._ttsPlayer.play(
      this.voicePreviewText(),
      `voice-preview-${voice}`,
      voice,
      lang,
    );
  }

  onVarOverrideChange(baseVarId: string, value: string) {
    this._varEdits.update((edits) => new Map(edits).set(baseVarId, value));
  }

  handleSubmit() {
    if (!this._clientId) {
      return;
    }

    if (this.form.invalid || this.submitted()) {
      return;
    }

    const data = this.mapToRequest(this.form.value as ClientSettingsModel);

    this.submitted.set(true);
    this.form.disable();

    // Override payload (existing overrides + new edits), only when one changed:
    // the save replaces the whole set, so sending it unchanged would only ask
    // for `var:update` on every save of the client's other settings.
    const overridePayload =
      this.canEditVars() && this.hasVarChanges() ? this._buildOverridePayload() : null;

    this._clients
      .update(this._clientId, data)
      .pipe(
        switchMap(() => {
          if (this._clientId && overridePayload) {
            return this._varService.updateClientVars(this._clientId, overridePayload);
          }
          return of(null);
        }),
        catchError((error) => {
          this._dialog.alert({
            title: 'Foutmelding',
            message:
              'Er is iets misgegaan bij het opslaan van de instellingen. Probeer het later nog eens',
            confirmText: 'OK',
          });

          return throwError(() => error);
        }),
        finalize(() => {
          this.submitted.set(false);
          this.form.enable();
        }),
        tap((updatedVars) => {
          this._clientStateService.updateState({
            name: data.name,
            config: {
              ...this._clientStateService.currentClient()?.config,
              ...data.config,
            },
          });

          // Update local var state and clear edits
          if (Array.isArray(updatedVars)) {
            this.clientVars.set(updatedVars);
          }
          this._varEdits.set(new Map());
        })
      )
      .subscribe({
        next: () => this._snackbar.open('Gegevens succesvol opgeslagen', 'OK'),
      });
  }

  mapFromResponse(client: Client): ClientSettingsModel {
    return {
      name: client.name,
      nickname: client.nickname,
      config: client.config,
    };
  }

  mapToRequest(data: ClientSettingsModel) {
    return {
      ...data,
    };
  }

  private _updateVoicesForLang(lang: string): void {
    const langEntry = VOICE_CATALOG.find((l) => l.lang === lang);
    const voices = langEntry?.voices ?? [];
    this.maleVoices.set(voices.filter((v) => v.gender === 'male'));
    this.femaleVoices.set(voices.filter((v) => v.gender === 'female'));
    this.voicePreviewText.set(langEntry?.previewText ?? '');
  }

  /** Every override once the edits are saved: the save replaces the whole set */
  private _buildOverridePayload() {
    const payload: { baseVarId: string; value: string }[] = [];

    for (const v of this.clientVars()) {
      const value = this._overrideAfterEdits(v);
      if (value !== null) {
        payload.push({ baseVarId: v.baseVarId, value });
      }
    }

    return payload;
  }

  /** The override `v` has saved; null for none */
  private _savedOverride(v: ClientVarResponse): string | null {
    return v.isOverridden ? v.value : null;
  }

  /**
   * The override `v` has once the edits are saved: what its field holds, where
   * an empty field or the base value means none -- clearing a field resets it.
   */
  private _overrideAfterEdits(v: ClientVarResponse): string | null {
    const edit = this._varEdits().get(v.baseVarId);
    if (edit === undefined) return this._savedOverride(v);
    return edit === '' || edit === v.baseValue ? null : edit;
  }
}
